import React, { useMemo, useState, useCallback, useEffect } from "react";
import TableV2 from "../../../../../../components/TableV2";
import TitleButtons from "../../../../../../components/TitleButtons";
import LeftTable from "../../../../../../components/LeftTable";
import { useStandbySuprimentos } from "./standbyData-consumo";
import Modal from "../../../../../../components/Modal";
import { generatePdfReport } from "../../../../../../utils/pdf/Report-Standby-Technical";
import { generatePdfReportAllOS } from "../../../../../../utils/pdf/Report-Standby-All-OS";

type Row = (string | number)[];

const IDX = {
  OS: 0,
  TIPO: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  TECNICO: 6,
  DATA: 7,
  HORA: 8,
};

type RawProduto = {
  codigo: string;
  nome: string;
  posicao?: string | null;
};

type RawItem = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: RawProduto;
};

type RawMov = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    tipo: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string;
    embarcacao?: string | null; // ✅ agora no tipo
  };
  itens: RawItem[];
};

const API_BASE = "https://api.eva-googlemarine.com";
const URL_LIST = `${API_BASE}/departamentos/suprimentos/standby`;
const URL_RETORNAR = `${API_BASE}/movimentar-produtos-retornar`;
const URL_BAIXAR = `${API_BASE}/movimentar-produtos-baixar`;

function parseBRL(s: any) {
  return typeof s === "string"
    ? Number(
        String(s)
          .replace(/[^\d,.-]/g, "")
          .replace(".", "")
          .replace(",", ".")
      )
    : Number(s ?? 0);
}

const SuprimentosStandby: React.FC = () => {
  // ============================================================
  // DEV_MODE: true = NÃO envia POST (mostra alert com payload)
  //           false = envia POST normalmente
  // ============================================================
  const DEV_MODE = false;
  // ============================================================

  const headers = useMemo(
    () => [
      "OS",
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Técnico",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "60px",
      "80px",
      "70px",
      "300px",
      "10px",
      "100px",
      "100px",
      "80px",
      "60px",
    ],
    []
  );

  const { rows, loading, refresh } = useStandbySuprimentos();

  const [openOS, setOpenOS] = useState<string | null>(null);
  const closeModal = useCallback(() => setOpenOS(null), []);

  const handleDoubleClick = useCallback(
    (rowIndex: number) => {
      const r = rows[rowIndex] as Row | undefined;
      if (!r) return;
      const os = String(r[IDX.OS] ?? "").trim();
      if (!os) return;
      setOpenOS(os);
    },
    [rows]
  );

  // ---------- RAW da O.S. (para ter movimentacao_id, responsavel_id, embarcacao, etc.) ----------
  const [rawMovs, setRawMovs] = useState<RawMov[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadRaw() {
      if (!openOS) return;
      setBusy(false);
      setSelectedIndex(null);
      try {
        const res = await fetch(URL_LIST, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? (json.data as RawMov[]) : [];
        const onlyThisOS = data.filter(
          (m) => (m.movimentacao.os_numero || "-") === openOS
        );
        if (!cancelled) setRawMovs(onlyThisOS);
      } catch (e) {
        console.error("Falha ao carregar detalhes RAW da O.S.:", e);
        if (!cancelled) setRawMovs(null);
      }
    }
    loadRaw();
    return () => {
      cancelled = true;
    };
  }, [openOS]);

  // ✅ Embarcação (pega do primeiro mov que tenha valor)
  const embarcacaoNome = useMemo(() => {
    if (!rawMovs?.length) return "-";
    const mv = rawMovs.find((m) => (m.movimentacao.embarcacao || "").trim());
    return (mv?.movimentacao.embarcacao || "-").trim();
  }, [rawMovs]);

  // ---------- Detalhes (UI) da O.S. a partir das linhas da tabela principal ----------
  const osDetails = useMemo(() => {
    if (!openOS) return null;
    const itens = (rows as Row[]).filter((r) => String(r[IDX.OS]) === openOS);
    if (!itens.length)
      return {
        info: [] as { label: string; value: string }[],
        headers: [] as string[],
        tableRows: [] as Row[],
        colWidths: [] as string[],
        totalMateriais: 0,
        totalUnidades: 0,
        totalCusto: 0,
      };

    const first = itens[0];
    const data = String(first[IDX.DATA] ?? "-");
    const hora = String(first[IDX.HORA] ?? "-");
    const tecnico = String(first[IDX.TECNICO] ?? "-");

    const totalUnidades = itens.reduce(
      (acc, r) => acc + Number(r[IDX.QTD] ?? 0),
      0
    );
    const totalMateriais = itens.length;
    const totalCusto = itens.reduce(
      (acc, r) => acc + parseBRL(r[IDX.CUSTO]) * Number(r[IDX.QTD] ?? 0),
      0
    );

    // ✅ Inserimos "Embarcação" logo abaixo de "Responsável/Técnico"
    const info = [
      { label: "Número da O.S", value: openOS },
      { label: "Responsável/Técnico", value: tecnico || "-" },
      { label: "Embarcação", value: embarcacaoNome || "-" }, // << aqui
      { label: "Data", value: data },
      { label: "Hora", value: hora },
      {
        label: "Total de Materiais Coletados",
        value: `${totalMateriais} itens, ${totalUnidades} unidades`,
      },
      {
        label: "Custo Total (aprox.)",
        value:
          isFinite(totalCusto) && totalCusto > 0
            ? totalCusto.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })
            : "-",
      },
    ];

    const detailsHeaders = [
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Responsável",
      "Data",
      "Hora",
    ];
    const detailsColWidths = [
      "80px",
      "100px",
      "350px",
      "90px",
      "180px",
      "90px",
      "70px",
    ];

    const tableRows = itens.map((r) => [
      String(r[IDX.TIPO] ?? "-"),
      String(r[IDX.CODIGO] ?? "-"),
      String(r[IDX.NOME] ?? "-"),
      String(r[IDX.QTD] ?? "-"),
      String(r[IDX.TECNICO] ?? "-"),
      String(r[IDX.DATA] ?? "-"),
      String(r[IDX.HORA] ?? "-"),
    ]);

    return {
      info,
      headers: detailsHeaders,
      colWidths: detailsColWidths,
      tableRows,
      totalMateriais,
      totalUnidades,
      totalCusto,
    };
  }, [openOS, rows, embarcacaoNome]); // ✅ depende de embarcacaoNome

  // ---------- Mapeia linhas UI → itens RAW equivalentes ----------
  const rawItemsForUI: (RawItem | null)[] = useMemo(() => {
    if (!openOS || !osDetails || !rawMovs) return [];
    const pool: (RawItem & { __used?: boolean })[] = rawMovs.flatMap(
      (m) => m.itens || []
    );
    return osDetails.tableRows.map((uiRow) => {
      const codigo = uiRow[1] as string;
      const nome = uiRow[2] as string;
      const qtd = Number(uiRow[3]);

      const found = pool.find(
        (it) =>
          !it.__used &&
          it.produto?.codigo === codigo &&
          it.produto?.nome === nome &&
          Number(it.quantidade) === qtd
      );
      if (found) {
        found.__used = true;
        return found;
      }
      const f2 = pool.find(
        (it) =>
          !it.__used &&
          it.produto?.codigo === codigo &&
          Number(it.quantidade) === qtd
      );
      if (f2) {
        f2.__used = true;
        return f2;
      }
      const f3 = pool.find((it) => !it.__used && it.produto?.codigo === codigo);
      if (f3) {
        f3.__used = true;
        return f3;
      }
      return null;
    });
  }, [openOS, osDetails, rawMovs]);

  // Responsável da O.S. (pega do primeiro mov válido)
  const responsavelId = useMemo(() => {
    if (!rawMovs?.length) return null;
    const withResp = rawMovs.find((m) => m.movimentacao.responsavel_id != null);
    return withResp?.movimentacao.responsavel_id ?? null;
  }, [rawMovs]);

  // ---------- Helpers ----------
  async function postJSON(url: string, payload: any) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} – ${t || "erro ao enviar"}`);
    }
    return res.json().catch(() => ({}));
  }

  async function askQuantidade(max: number): Promise<number | null> {
    const val = window.prompt(
      `Quantidade (máxima: ${max})`,
      String(Math.min(1, max))
    );
    if (val == null) return null;
    const n = Number(String(val).replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      window.alert("Informe um número válido maior que zero.");
      return null;
    }
    if (!Number.isInteger(n)) {
      window.alert("A quantidade deve ser inteira.");
      return null;
    }
    if (n > max) {
      window.alert(`A quantidade não pode exceder ${max}.`);
      return null;
    }
    return n;
  }

  // ---------- Ações (com alert no modo DEV) ----------
  const handleGerarRelatorioDaOs = useCallback(async () => {
    if (!openOS || !osDetails) {
      return window.alert("Abra uma O.S. para gerar o relatório completo.");
    }
    if (!rawMovs || !rawMovs.length) {
      return window.alert("Dados RAW da O.S. indisponíveis.");
    }

    const allItems = rawMovs.flatMap((m) =>
      (m.itens || []).map((it) => ({
        codigo: it.produto?.codigo || "-",
        nome: it.produto?.nome || "-",
        quantidade: Number(it.quantidade ?? 0),
        custoUnitario: (() => {
          const rawStr = it.custo_unitario;
          if (rawStr == null) return null;
          const n = Number(String(rawStr).replace(",", "."));
          return Number.isFinite(n) ? n : null;
        })(),
        tecnico: m.movimentacao.coletor_nome || "-",
      }))
    );

    const tecnicosSet = new Set<string>();
    rawMovs.forEach((m) => {
      const t = m.movimentacao.coletor_nome || ("" as string);
      if (t && t.trim()) tecnicosSet.add(t.trim());
    });
    const tecnicos = Array.from(tecnicosSet);

    try {
      await generatePdfReportAllOS({
        osNumero: openOS,
        tecnicos,
        materiais: allItems,
      });
    } catch (e: any) {
      window.alert(
        `Falha ao gerar o relatório completo da O.S.: ${e?.message || e}`
      );
    }
  }, [openOS, osDetails, rawMovs]);

  const handleGerarRelatorio = useCallback(async () => {
    if (!openOS || !osDetails) {
      return window.alert(
        "Abra uma O.S. e selecione um item para gerar o relatório."
      );
    }
    if (selectedIndex == null) {
      return window.alert(
        "Selecione um material na lista para gerar o relatório."
      );
    }
    if (!rawMovs || !rawMovs.length) {
      return window.alert("Dados RAW da O.S. indisponíveis.");
    }

    const tecnicoNome =
      rawMovs.find((m) => m.movimentacao.coletor_nome)?.movimentacao
        .coletor_nome ||
      (osDetails.tableRows[selectedIndex]?.[4] as string) ||
      "-";

    const ui = osDetails.tableRows[selectedIndex];
    if (!ui) return window.alert("Item selecionado não encontrado.");

    const codigo = String(ui[1] ?? "-");
    const nome = String(ui[2] ?? "-");
    const quantidade = Number(ui[3] ?? 0);

    const raw = rawItemsForUI[selectedIndex];
    const custoUnitarioParsed = (() => {
      const rawStr = raw?.custo_unitario;
      if (rawStr == null) return null;
      const n = Number(String(rawStr).replace(",", "."));
      return Number.isFinite(n) ? n : null;
    })();

    const _subtotal =
      custoUnitarioParsed != null && Number.isFinite(custoUnitarioParsed)
        ? quantidade * Number(custoUnitarioParsed)
        : null;

    try {
      await generatePdfReport({
        osNumero: openOS,
        tecnicoNome,
        materiais: [
          {
            codigo,
            nome,
            quantidade,
          },
        ],
      });
    } catch (e: any) {
      window.alert(`Falha ao gerar o relatório: ${e?.message || e}`);
    }
  }, [openOS, osDetails, selectedIndex, rawItemsForUI, rawMovs]);

  const handleRetornarItem = useCallback(async () => {
    if (!openOS || !osDetails) return;
    if (selectedIndex == null)
      return window.alert("Selecione um material na lista.");
    if (!rawMovs || responsavelId == null)
      return window.alert("Dados da O.S. indisponíveis (sem responsável).");

    const rawItem = rawItemsForUI[selectedIndex];
    if (!rawItem)
      return window.alert("Não foi possível localizar o item selecionado.");

    const max = Number(rawItem.quantidade ?? 0);
    if (!(max > 0))
      return window.alert("Item sem quantidade disponível para retorno.");

    const qtd = await askQuantidade(max);
    if (qtd == null) return;

    const payload = {
      responsavel_id: responsavelId,
      itens: [
        {
          movimentacao_id: rawItem.movimentacao_id,
          produto_id: rawItem.produto_id,
          quantidade: qtd,
        },
      ],
    };

    if (DEV_MODE) {
      alert("📦 Retornar ITEM payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, payload);
      window.alert("Retorno realizado com sucesso.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openOS,
    osDetails,
    selectedIndex,
    rawItemsForUI,
    responsavelId,
    refresh,
    rawMovs,
  ]);

  const handleBaixarItem = useCallback(async () => {
    if (!openOS || !osDetails) return;
    if (selectedIndex == null)
      return window.alert("Selecione um material na lista.");
    if (!rawMovs || responsavelId == null)
      return window.alert("Dados da O.S. indisponíveis (sem responsável).");

    const rawItem = rawItemsForUI[selectedIndex];
    if (!rawItem)
      return window.alert("Não foi possível localizar o item selecionado.");
    const max = Number(rawItem.quantidade ?? 0);
    if (!(max > 0))
      return window.alert("Item sem quantidade disponível para baixa.");

    const qtd = await askQuantidade(max);
    if (qtd == null) return;

    const payload = {
      responsavel_id: responsavelId,
      itens: [
        {
          movimentacao_id: rawItem.movimentacao_id,
          produto_id: rawItem.produto_id,
          quantidade: qtd,
        },
      ],
    };

    if (DEV_MODE) {
      alert("📦 Baixar ITEM payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_BAIXAR, payload);
      window.alert("Baixa realizada com sucesso.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao dar baixa no item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [
    openOS,
    osDetails,
    selectedIndex,
    rawItemsForUI,
    responsavelId,
    refresh,
    rawMovs,
  ]);

  const handleRetornarOSInteira = useCallback(async () => {
    if (!openOS || !rawMovs) return;
    if (responsavelId == null)
      return window.alert("Sem responsável definido para esta O.S.");

    const itens = rawMovs
      .flatMap((m) => m.itens || [])
      .filter((it) => Number(it.quantidade) > 0)
      .map((it) => ({
        movimentacao_id: it.movimentacao_id,
        produto_id: it.produto_id,
        quantidade: Number(it.quantidade),
      }));

    if (!itens.length)
      return window.alert("Não há itens com quantidade para retornar.");

    const payload = { responsavel_id: responsavelId, itens };

    if (DEV_MODE) {
      alert("📦 Retornar O.S. payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, payload);
      window.alert("Todos os itens foram retornados ao estoque.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar O.S.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openOS, rawMovs, responsavelId, refresh]);

  const handleBaixarOSInteira = useCallback(async () => {
    if (!openOS || !rawMovs) return;
    if (responsavelId == null)
      return window.alert("Sem responsável definido para esta O.S.");

    const itens = rawMovs
      .flatMap((m) => m.itens || [])
      .filter((it) => Number(it.quantidade) > 0)
      .map((it) => ({
        movimentacao_id: it.movimentacao_id,
        produto_id: it.produto_id,
        quantidade: Number(it.quantidade),
      }));

    if (!itens.length)
      return window.alert("Não há itens com quantidade para dar baixa.");

    const payload = { responsavel_id: responsavelId, itens };

    if (DEV_MODE) {
      alert("📦 Baixar O.S. payload:\n\n" + JSON.stringify(payload, null, 2));
      return;
    }

    try {
      setBusy(true);
      await postJSON(URL_BAIXAR, payload);
      window.alert("Todos os itens tiveram baixa realizada.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao dar baixa na O.S.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openOS, rawMovs, responsavelId, refresh]);

  const handleModalClose = useCallback(() => {
    if (busy) return;
    closeModal();
  }, [busy, closeModal]);

  return (
    <div>
      {DEV_MODE && (
        <div
          style={{
            background: "#FFF4E5",
            border: "1px dashed #B26A00",
            color: "#613400",
            padding: "8px 12px",
            margin: "8px 16px",
            borderRadius: 4,
            fontSize: 14,
          }}
        >
          ⚠️ <b>Modo DEV ativo:</b> nenhuma requisição será enviada. Os botões
          exibem um <code>alert</code> com o <code>payload</code>.
        </div>
      )}

      <TitleButtons
        title="Suprimentos - Standby"
        buttons={[
          {
            label: loading ? "Carregando..." : "Gerar Relatório",
            onClick: () => console.log("Relatório gerado"),
            variant: "primary",
          },
        ]}
      />

      <TableV2
        headers={headers}
        rows={rows}
        columnWidths={columnWidths}
        isTableStandby
        onDoubleClick={handleDoubleClick}
        responsiveWidths={{
          1300: {
            1: "60px",
            2: "0px",
            3: "300px",
            4: "0px",
            5: "100px",
            6: "100px",
            7: "60px",
            8: "60px",
          },
        }}
      />

      {/* ---- Modal centralizado e sobreposto ---- */}
      <Modal open={!!openOS} onClose={handleModalClose} maxWidth={1280}>
        {openOS && osDetails && (
          <div
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 8,
              width: "100%",
            }}
          >
            {/* Bloco informativo (agora com Embarcação) */}
            <div style={{ width: "100%" }}>
              <LeftTable data={osDetails.info} />
            </div>

            {/* Ações da O.S. inteira */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              <button
                disabled={busy || !rawMovs || responsavelId == null}
                onClick={handleRetornarOSInteira}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #0a7",
                  background: "#f0fffa",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna todos os itens desta O.S. ao estoque"
              >
                Retornar O.S. ao estoque
              </button>
              <button
                disabled={busy || !rawMovs || responsavelId == null}
                onClick={handleBaixarOSInteira}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #a00",
                  background: "#fff5f5",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Dá baixa em todos os itens desta O.S."
              >
                Dar baixa na O.S.
              </button>
              <button
                disabled={busy || !rawMovs || responsavelId == null}
                onClick={handleGerarRelatorioDaOs}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #C27B30FF",
                  background: "#FCF1E5FF",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Gerar relatório completo da O.S."
              >
                Gerar Relatório da O.S
              </button>
            </div>

            {/* Tabela de materiais com seleção */}
            <div
              style={{
                width: "100%",
                overflowX: "auto",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <div style={{ minWidth: 980 }}>
                {/* Cabeçalho */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: osDetails.colWidths.join(" "),
                    fontWeight: 300,
                    padding: "10px 10px",
                    borderBottom: "1px solid #264777",
                    borderLeft: "1px solid #264777",
                    borderRight: "1px solid #264777",
                    borderTop: "1px solid #264777",
                    background: "#284a7c",
                    color: "#FFFFFFFF",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  {osDetails.headers.map((h, idx) => {
                    const centerCols = [0, 1, 3, 4, 6, 7];
                    const textAlign = centerCols.includes(idx)
                      ? "center"
                      : "left";
                    return (
                      <div key={h} style={{ textAlign }}>
                        {h}
                      </div>
                    );
                  })}
                </div>

                {/* Linhas */}
                {osDetails.tableRows.map((r, i) => {
                  const selected = i === selectedIndex;
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedIndex(i)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: osDetails.colWidths.join(" "),
                        padding: "12px 10px",
                        borderBottom: "1px solid #eee",
                        background: selected ? "#e6f0ff" : "white",
                        cursor: "pointer",
                        userSelect: "none",
                        fontFamily: "Arial, sans-serif",
                      }}
                      title="Clique para selecionar"
                    >
                      {r.map((c, j) => {
                        const centerCols = [0, 1, 3, 4, 6, 7];
                        const textAlign = centerCols.includes(j)
                          ? "center"
                          : "left";
                        return (
                          <div
                            key={j}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign,
                            }}
                            title={String(c)}
                          >
                            {c}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ações por item */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 8,
              }}
            >
              <button
                disabled={
                  busy ||
                  selectedIndex == null ||
                  !rawMovs ||
                  responsavelId == null
                }
                onClick={handleRetornarItem}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #08a",
                  background: "#f0f8ff",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna somente o item selecionado"
              >
                Retornar produto ao estoque
              </button>
              <button
                disabled={
                  busy ||
                  selectedIndex == null ||
                  !rawMovs ||
                  responsavelId == null
                }
                onClick={handleBaixarItem}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #a00",
                  background: "#fff5f5",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Dá baixa somente no item selecionado"
              >
                Dar baixa no item
              </button>
              <button
                disabled={
                  busy ||
                  selectedIndex == null ||
                  !rawMovs ||
                  responsavelId == null
                }
                onClick={handleGerarRelatorio}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #C27B30FF",
                  background: "#FCF1E5FF",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Gerar relatório da movimentação"
              >
                Gerar Relatório da Movimentação
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuprimentosStandby;
